import { Component, computed, inject, signal, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { SharedModule } from '../../../../zentility/_shared/shared.module';
import { BillAiFlowStore } from '../state/bill-ai-flow.store';
import { LegalEntityMatchResult, ServiceAddressMatchResult, BillMatchResult } from '../../../models/bill-import.models';
import { StateV4Service } from '../../../../zentility/_shared/_services/v4/state-v4.service';
import { take } from 'rxjs/operators';
import { formatBillPeriod } from '../../../utils/date-format.util';
import { MIN_DISPLAY_SCORE, getScoreClass } from '../../../utils/match-score.util';
import { EntityMatchSelectorComponent } from '../entity-match-selector/entity-match-selector.component';
import { ScoreSelectComponent, ScoreSelectOption } from '../score-select/score-select.component';
import { ParentContextBannerComponent } from '../parent-context-banner/parent-context-banner.component';
import { ParentConflictResolverComponent } from '../parent-context-banner/parent-conflict-resolver.component';
import { MergeModalComponent } from '../../merge-modal/merge-modal.component';
import { SelectionActionBarComponent } from '../../selection-action-bar/selection-action-bar.component';
import { validateLenStep, LenValidationInput, LenAddress } from './bill-ai-flow-len-step.validation';
import { StepValidationResult, ValidationSummary, buildFieldErrorMaps } from '../validation';

@Component({
  selector: 'app-bill-ai-flow-len-step',
  standalone: true,
  imports: [CommonModule, FormsModule, MatFormFieldModule, MatInputModule, MatSelectModule, MatCheckboxModule, SharedModule, EntityMatchSelectorComponent, ScoreSelectComponent, ParentContextBannerComponent, ParentConflictResolverComponent, MergeModalComponent, SelectionActionBarComponent],
  templateUrl: './bill-ai-flow-len-step.component.html',
  styleUrl: './bill-ai-flow-len-step.component.scss'
})
export class BillAiFlowLenStepComponent {
  stateOptions: string[] = [];

  getUtilities(sa: ServiceAddressMatchResult): string {
    const utils = new Set<string>();
    sa.meters.forEach(m => {
      if (m.utility && m.utility.utilityName) {
        utils.add(m.utility.utilityName);
      }
    });
    return Array.from(utils).join(', ') || 'Unknown Utility';
  }

  getSaCounts(sa: ServiceAddressMatchResult) {
    let meterCount = sa.meters.length;
    let billCount = 0;
    sa.meters.forEach(m => billCount += m.bills.length);
    return { meterCount, billCount };
  }

  getConfidence(sa: ServiceAddressMatchResult): number {
    return sa.match ? Math.round(sa.match.score * 100) : 0;
  }

  previewBill(sa: ServiceAddressMatchResult) {
    const bills = this.saBillsMap[sa.groupId] || [];
    if (bills.length === 1) {
      this.previewSpecificBill(bills[0]);
    } else if (bills.length > 1) {
      this.toggleBillExpansion(sa.groupId);
    }
  }

  toggleBillExpansion(saGroupId: string): void {
    this.expandedBillSas[saGroupId] = !this.expandedBillSas[saGroupId];
  }

  previewSpecificBill(bill: BillMatchResult): void {
    const billFile = this.state.getBillFileForGroup(bill.groupId);
    if (billFile) {
      this.state.setPreviewBillFile(billFile);
    }
  }

  getBillLabel(bill: BillMatchResult): string {
    if (this.billLabelMap[bill.groupId]) return this.billLabelMap[bill.groupId];
    const data = this.state.getBillData(bill.groupId);
    const label = formatBillPeriod(data?.billingPeriodStart, data?.billingPeriodEnd, bill.groupId);
    this.billLabelMap[bill.groupId] = label;
    return label;
  }

  getLenAddress(groupId: string): string {
    const edited = this.editableBillingAddresses().get(groupId);
    if (edited) {
      const parts = [edited.street1, edited.street2, edited.city, edited.state, edited.zip].filter(Boolean);
      return parts.length > 0 ? parts.join(', ') : 'No billing address provided';
    }
    return this.state.getLenBillingAddress(groupId) || 'No billing address provided';
  }

  getLenAddressData(groupId: string): LenAddress {
    const edited = this.editableBillingAddresses().get(groupId);
    if (edited) return edited;

    // Parse from state or match request
    const request = this.state.matchRequest();
    if (request) {
      for (const len of request.legalEntities) {
        if (len.groupId === groupId && len.billingAddress) {
          return {
            street1: len.billingAddress.street1 || '',
            street2: len.billingAddress.street2 || '',
            city: len.billingAddress.city || '',
            state: len.billingAddress.state || '',
            zip: len.billingAddress.zip || ''
          };
        }
      }
    }
    return { street1: '', street2: '', city: '', state: '', zip: '' };
  }

  toggleEditBilling(groupId: string, event: Event): void {
    event.stopPropagation();
    const current = new Set(this.editingBillingAddresses());
    if (current.has(groupId)) {
      current.delete(groupId);
    } else {
      current.add(groupId);
    }
    this.editingBillingAddresses.set(current);
  }

  /**
   * Click-to-fix from the display-mode billing address. Expands the edit
   * panel if collapsed and focuses the specific input so the user can
   * immediately start typing. Called from the `<span class="field-missing">`
   * click handlers — matches the SA step's pattern.
   */
  onMissingBillingFieldClick(groupId: string, field: keyof LenAddress, event: Event): void {
    event.stopPropagation();
    const current = new Set(this.editingBillingAddresses());
    if (!current.has(groupId)) {
      current.add(groupId);
      this.editingBillingAddresses.set(current);
    }
    queueMicrotask(() => {
      const el = document.getElementById(`len-${groupId}-${field}`);
      if (el) (el as HTMLElement).focus();
    });
  }

  isEditingBilling(groupId: string): boolean {
    return this.editingBillingAddresses().has(groupId);
  }

  onBillingFieldChange(groupId: string, field: keyof LenAddress, value: string): void {
    const updated = new Map(this.editableBillingAddresses());
    const currentData = this.getLenAddressData(groupId);
    const patched: LenAddress = { ...currentData, [field]: value };
    updated.set(groupId, patched);
    this.editableBillingAddresses.set(updated);
    // Sync back to lenAddressDataMap so validation picks up the change
    this.lenAddressDataMap[groupId] = patched;
    // Persist to the store so buildImportRequest sees the edit
    this.state.updateLenBillingAddress(groupId, { [field]: value });
    this.revalidate();
  }

  // Track which LENs are expanded to show children
  expandedLens = signal<Set<string>>(new Set());

  // Track which billing addresses are in edit mode
  editingBillingAddresses = signal<Set<string>>(new Set());

  // Track edited billing addresses
  editableBillingAddresses = signal<Map<string, LenAddress>>(new Map());

  // Pre-computed values for template
  readonly state = inject(BillAiFlowStore);
  private stateV4Svc = inject(StateV4Service);
  legalEntities = computed(() => this.state.matchResponse()?.legalEntities ?? []);
  mergeSelection = this.state.lenMergeSelection;
  isNewCustomer = this.state.isNewCustomer;
  canMerge = computed(() => this.mergeSelection().length >= 2);
  identicalLensSafe = this.state.identicalLensSafe;
  identicalLensDestructive = this.state.identicalLensDestructive;
  hasBlockingLenDuplicate = this.state.hasBlockingLenDuplicate;

  /**
   * LEN groupIds that Express Import flagged as blocking fast-track on this
   * step. Used by the template to paint a "needs attention" outline on the
   * offending rows so the user's eye lands there immediately. Empty unless
   * all three hold: fast-track mode is 'fast', the LEN step wasn't marked
   * skipped during execution, and the analyzer recorded blockers here.
   */
  fastTrackBlockingSet = computed<Set<string>>(() => {
    if (this.state.fastTrackMode() !== 'fast') return new Set();
    if (this.state.fastTrackSkippedSteps().includes('len')) return new Set();
    const ids = this.state.fastTrackAnalysis()?.blockingGroupIds?.['len'] ?? [];
    return new Set(ids);
  });

  // Pre-computed lookup maps for template (avoids method calls in templates)
  saCountsMap: Record<string, { meterCount: number; billCount: number }> = {};
  saConfidenceMap: Record<string, number> = {};
  saUtilitiesMap: Record<string, string> = {};
  lenAddressDataMap: Record<string, LenAddress> = {};
  displayNameMap: Record<string, string> = {};
  /** Exact in-scope LEN name hit — linked to the existing record, Create New disabled. */
  lenExactLockedMap: Record<string, boolean> = {};
  lenAddressDisplayMap: Record<string, string> = {};
  mergeSelectedMap: Record<string, boolean> = {};
  editingBillingMap: Record<string, boolean> = {};
  expandedBillSas: Record<string, boolean> = {};
  previewingBillMap: Record<string, boolean> = {};
  saBillsMap: Record<string, BillMatchResult[]> = {};
  billLabelMap: Record<string, string> = {};
  lenScoreOptionsMap: Record<string, ScoreSelectOption[]> = {};
  lenSelectionMap: Record<string, number | string | null> = {};
  /** lenGroupId → 'safe-info' (will auto-merge) | 'destructive-error' (data loss) | undefined */
  lenDuplicateKindMap: Record<string, 'safe-info' | 'destructive-error' | undefined> = {};
  /** Pre-computed message text per LEN, matching lenDuplicateKindMap. */
  lenDuplicateMessageMap: Record<string, string> = {};

  // Merge modal state
  mergeModalState: {
    isOpen: boolean;
    candidates: string[];
    labelMap: Record<string, string>;
    subLabelMap: Record<string, string>;
  } = {
    isOpen: false, candidates: [], labelMap: {}, subLabelMap: {}
  };
  canUndoMerge = this.state.canUndoLenMerge;
  canUndoDelete = this.state.canUndoLenDelete;

  constructor() {
    this.stateV4Svc.getAllCountryStates().pipe(take(1)).subscribe({
      next: states => this.stateOptions = states.map(s => s.key),
    });
    // Rebuild lookup maps when match data changes
    effect(() => {
      const response = this.state.matchResponse();
      // Track signals that influence validation so revalidate runs automatically
      this.state.entityNameOverrides();
      this.state.entitySelections();
      if (response) {
        this.rebuildLookupMaps();
        this.revalidate();
      }
    });
    // Track which bill is currently being previewed
    effect(() => {
      const previewId = this.state.previewBillFileId();
      const mapping = this.state.billFileIdMapping();
      const map: Record<string, boolean> = {};
      for (const [groupId, fileId] of Object.entries(mapping)) {
        map[groupId] = fileId === previewId;
      }
      this.previewingBillMap = map;
    });
    // Track merge selection and editing billing changes
    effect(() => {
      const selection = this.mergeSelection();
      const editing = this.editingBillingAddresses();
      const response = this.state.matchResponse();
      const selections = this.state.entitySelections();
      if (response) {
        for (const len of response.legalEntities) {
          this.mergeSelectedMap[len.groupId] = selection.includes(len.groupId);
          this.editingBillingMap[len.groupId] = editing.has(len.groupId);
          this.lenSelectionMap[len.groupId] = selections[len.groupId] ?? null;
        }
      }
    });
    // Track LEN duplicate-warning state
    effect(() => {
      const safe = this.state.identicalLensSafe();
      const destructive = this.state.identicalLensDestructive();
      const kindMap: Record<string, 'safe-info' | 'destructive-error' | undefined> = {};
      const messages: Record<string, string> = {};
      for (const g of safe) {
        for (const id of g.lenGroupIds) {
          kindMap[id] = 'safe-info';
          messages[id] = 'This legal entity is identical to another and will be automatically merged on import.';
        }
      }
      for (const g of destructive) {
        for (const id of g.lenGroupIds) {
          kindMap[id] = 'destructive-error';
          messages[id] =
            `These legal entities have identical names but different billing addresses. They will be merged on import and one billing address will be discarded. Merge the Legal Entities, or edit the names so they're different.`;
        }
      }
      this.lenDuplicateKindMap = kindMap;
      this.lenDuplicateMessageMap = messages;
    });
  }

  /** Rebuild all pre-computed lookup maps for template binding */
  private rebuildLookupMaps(): void {
    const response = this.state.matchResponse();
    if (!response) return;

    this.lenExactLockedMap = {};

    for (const len of response.legalEntities) {
      this.lenAddressDataMap[len.groupId] = this.getLenAddressData(len.groupId);
      this.displayNameMap[len.groupId] = this.state.getOriginalName(len.groupId);
      // Exact in-scope name hit: linked; creating a duplicate LEN is never offered.
      this.lenExactLockedMap[len.groupId] = !this.isNewCustomer() && len.exactMatch === true;
      this.lenAddressDisplayMap[len.groupId] = this.getLenAddress(len.groupId);

      // Build score options for LEN (filter out matches below threshold)
      const options: ScoreSelectOption[] = [];
      if (len.match && len.match.score >= MIN_DISPLAY_SCORE) {
        const pct = Math.round(len.match.score * 100);
        options.push({
          value: len.match.id,
          label: len.match.label,
          score: pct,
          scoreClass: getScoreClass(pct)
        });
      }
      for (const alt of len.alternatives || []) {
        if (alt.score < MIN_DISPLAY_SCORE) continue;
        if (!len.match || alt.id !== len.match.id) {
          const pct = Math.round(alt.score * 100);
          options.push({
            value: alt.id,
            label: alt.label,
            score: pct,
            scoreClass: getScoreClass(pct)
          });
        }
      }
      this.lenScoreOptionsMap[len.groupId] = options;

      for (const sa of len.serviceAddresses) {
        this.saCountsMap[sa.groupId] = this.getSaCounts(sa);
        this.saConfidenceMap[sa.groupId] = this.getConfidence(sa);
        this.saUtilitiesMap[sa.groupId] = this.getUtilities(sa);
        this.displayNameMap[sa.groupId] = this.state.getOriginalName(sa.groupId);
        // Pre-compute flat bill list and labels for each SA (across all its meters)
        const bills: BillMatchResult[] = [];
        for (const meter of sa.meters) {
          for (const bill of meter.bills) {
            bills.push(bill);
            const data = this.state.getBillData(bill.groupId);
            this.billLabelMap[bill.groupId] = formatBillPeriod(data?.billingPeriodStart, data?.billingPeriodEnd, bill.groupId);
          }
        }
        this.saBillsMap[sa.groupId] = bills;
      }
    }
  }

  // --- Expand/Collapse ---

  toggleExpand(groupId: string): void {
    const current = new Set(this.expandedLens());
    if (current.has(groupId)) {
      current.delete(groupId);
    } else {
      current.add(groupId);
    }
    this.expandedLens.set(current);
  }

  isExpanded(groupId: string): boolean {
    return this.expandedLens().has(groupId);
  }

  // --- Editable Names ---

  getDisplayName(groupId: string): string {
    return this.state.getOriginalName(groupId);
  }

  onNameChange(groupId: string, newName: string): void {
    this.state.updateEntityName(groupId, newName);
  }

  // --- Merge ---

  toggleMergeSelection(groupId: string): void {
    this.state.toggleLenMergeSelection(groupId);
  }

  isMergeSelected(groupId: string): boolean {
    return this.mergeSelection().includes(groupId);
  }

  openMergeModal(): void {
    const mergeIds = this.mergeSelection();
    if (mergeIds.length < 2) return;

    const labelMap: Record<string, string> = {};
    const subLabelMap: Record<string, string> = {};
    for (const groupId of mergeIds) {
      labelMap[groupId] = this.state.getOriginalName(groupId);
      // Billing addresses can differ between the candidates — surface them so
      // the user knows which one survives the merge (the selected LEN's).
      const address = this.state.getLenBillingAddress(groupId);
      subLabelMap[groupId] = address ? `Billing address: ${address}` : 'Billing address: —';
    }

    this.mergeModalState = { isOpen: true, candidates: [...mergeIds], labelMap, subLabelMap };
  }

  closeMergeModal(): void {
    this.mergeModalState = { ...this.mergeModalState, isOpen: false };
  }

  onMergeConfirmed(targetGroupId: string): void {
    this.closeMergeModal();
    this.state.mergeLens(targetGroupId);
  }

  undoMerge(): void {
    this.state.undoLastLenMerge();
  }

  // Last-item delete confirmation
  showLastItemConfirm = false;

  deleteSelected(): void {
    if (this.state.wouldDeleteAll('len')) {
      this.showLastItemConfirm = true;
      return;
    }
    this.state.deleteLens();
    this.rebuildLookupMaps();
  }

  confirmLastItemDelete(): void {
    this.showLastItemConfirm = false;
    this.state.requestClose();
  }

  cancelLastItemDelete(): void {
    this.showLastItemConfirm = false;
  }

  undoDelete(): void {
    this.state.undoLastLenDelete();
    this.rebuildLookupMaps();
  }

  // --- Match Selection ---

  onSelectionChange(groupId: string, value: number | string | null): void {
    this.state.updateSelection(groupId, value);
  }

  // --- Refresh ---

  isRefreshing = signal(false);

  async refreshMatches(): Promise<void> {
    const customerId = this.state.selectedCustomerId();
    if (customerId === null) return;

    this.isRefreshing.set(true);
    try {
      await this.state.runMatch(Number(customerId), { reuseRequest: true });
    } finally {
      this.isRefreshing.set(false);
    }
  }

  // --- Navigation ---

  goBack(): void {
    this.state.goBack();
  }

  async goForward(): Promise<void> {
    if (this.state.isMatching()) return; // re-entry guard: a re-match is already in flight
    if (this.hasBlockingLenDuplicate()) return; // soft-block: destructive merge would discard a billing address
    const result = this.runValidation();
    this.rebuildFieldErrors(result);
    if (result.hasBlockers) return;

    // Committed selections narrow downstream candidate pools — re-fire the
    // match before the analyzer so cascade + pause banners on downstream
    // steps reflect the new scope.
    if (this.state.needsRematch()) {
      await this.state.runMatch(Number(this.state.selectedCustomerId()), { reuseRequest: true });
      if (this.state.error()) return; // stay put — navigating would clear the error banner
    }
    this.state.completeStep('len');
    this.state.analyzeFastTrack();
    const next = this.state.getNextStep();
    if (next) {
      this.state.goToStep(next);
    }
  }

  // --- Validation state (see guides/ai-onboarding/FIELD-VALIDATION.md) ---
  fieldErrors: Record<string, Record<string, boolean>> = {};
  hasEntityErrors: Record<string, boolean> = {};
  lenBillingAddressIncomplete: Record<string, boolean> = {};
  validationSummary: ValidationSummary = { errorGroups: [], warningGroups: [], totalErrors: 0, totalWarnings: 0 };

  private runValidation(): StepValidationResult {
    const input: LenValidationInput = {
      legalEntities: this.state.matchResponse()?.legalEntities ?? [],
      entityNameOverrides: this.state.entityNameOverrides(),
      extractedLenNames: this.buildExtractedLenNames(),
      lenAddressDataMap: this.lenAddressDataMap,
      isNewEntity: (gid) => this.state.isNewEntity(gid),
      selections: this.state.entitySelections(),
    };
    return validateLenStep(input);
  }

  private buildExtractedLenNames(): Record<string, string> {
    const map: Record<string, string> = {};
    const req = this.state.matchRequest();
    if (!req) return map;
    for (const len of req.legalEntities) {
      if (len.groupId && len.name) {
        map[len.groupId] = len.name;
      }
    }
    return map;
  }

  private rebuildFieldErrors(result: StepValidationResult): void {
    const maps = buildFieldErrorMaps(result);
    this.fieldErrors = maps.byField;
    this.hasEntityErrors = maps.byEntity;
    this.validationSummary = maps.summary;

    // LEN-specific: aggregate billing address field errors into a single
    // per-LEN flag the template uses to show "Billing address incomplete".
    const billingIncomplete: Record<string, boolean> = {};
    for (const err of result.errors) {
      if (err.field.startsWith('billingAddress.')) {
        billingIncomplete[err.entityId] = true;
      }
    }
    this.lenBillingAddressIncomplete = billingIncomplete;
  }

  revalidate(): void {
    this.rebuildFieldErrors(this.runValidation());
  }
}
